import React, { useState, useEffect } from "react";
import { 
  Card, CardContent, CardDescription, CardHeader, CardTitle 
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Dialog, DialogContent, DialogDescription, DialogHeader, 
  DialogTitle, DialogFooter 
} from "../components/ui/dialog";
import { 
  Table, TableBody, TableCell, TableHead, 
  TableHeader, TableRow 
} from "../components/ui/table";
import { 
  Users, CreditCard, FileText, Settings, Mail, 
  Plus, Edit, Trash2, Loader2, Search, DollarSign,
  CheckCircle, XCircle, Ban, Send, TrendingUp, ShieldCheck,
  Bell
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDateTime, cn } from "../lib/utils";
import { toast } from "sonner";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState("users");

  return (
    <div className="min-h-screen bg-slate-50/50 p-1 md:p-8 font-sans">
      <div className="max-w-[1400px] mx-auto space-y-8 animate-in fade-in duration-700">
        
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">Admin Panel</h1>
            <p className="text-slate-500 font-medium mt-1">Manage activities on this website</p>
          </div>
          <div className="flex items-center gap-3">
             <Badge variant="outline" className="bg-white border-slate-200 text-slate-500 px-4 py-1.5 rounded-full shadow-sm">
                <ShieldCheck className="w-3.5 h-3.5 mr-2 text-blue-600" /> Administrative Access
             </Badge>
          </div>
        </div>

        {/* Global Tabs Navigation */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="h-14 w-full justify-start overflow-x-auto rounded-[1.25rem] bg-slate-200/50 p-1.5 backdrop-blur-md border border-slate-200 no-scrollbar">
            <TabTrigger value="users" icon={<Users />} label="Users" />
            <TabTrigger value="transactions" icon={<TrendingUp />} label="Transactions" />
            <TabTrigger value="loans" icon={<FileText />} label="Loans" />
            <TabTrigger value="cards" icon={<CreditCard />} label="Cards" />
            <TabTrigger value="emails" icon={<Mail />} label="Emails" />
            <TabTrigger value="settings" icon={<Settings />} label="Settings" />
          </TabsList>

          <TabsContent value="users" className="outline-none"><UsersTab /></TabsContent>
          <TabsContent value="transactions" className="outline-none"><TransactionsTab /></TabsContent>
          <TabsContent value="loans" className="outline-none"><LoansTab /></TabsContent>
          <TabsContent value="cards" className="outline-none"><CardsTab /></TabsContent>
          <TabsContent value="emails" className="outline-none"><EmailsTab /></TabsContent>
          <TabsContent value="settings" className="outline-none"><SettingsTab /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

/* --- SHARED COMPONENTS --- */

const TabTrigger = ({ value, icon, label }) => (
  <TabsTrigger 
    value={value} 
    className="rounded-xl px-5 py-2.5 text-sm font-bold transition-all data-[state=active]:bg-white data-[state=active]:text-slate-900 data-[state=active]:shadow-sm text-slate-500 flex items-center gap-2"
  >
    {React.cloneElement(icon, { size: 16 })}
    {label}
  </TabsTrigger>
);

const SectionCard = ({ title, description, children, rightElement }) => (
  <Card className="border-none shadow-2xl shadow-slate-200/60 rounded-[2.5rem] bg-white overflow-hidden">
    <CardHeader className="px-8 pt-8 pb-4">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <CardTitle className="text-lg font-bold text-slate-900">{title}</CardTitle>
          <CardDescription className="text-slate-400 font-medium">{description}</CardDescription>
        </div>
        {rightElement}
      </div>
    </CardHeader>
    <CardContent className="p-6">{children}</CardContent>
  </Card>
);

/* --- TAB COMPONENTS --- */

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addTxnDialogOpen, setAddTxnDialogOpen] = useState(false);
  const [addNotDialogOpen, setAddNotDialogOpen] = useState(false);

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/users`);
      setUsers(response.data);
    } catch (error) { toast.error("Database fetch failed"); }
    finally { setLoading(false); }
  };

  const filteredUsers = users.filter(u =>
    `${u.first_name} ${u.last_name} ${u.email} ${u.account_number}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <SectionCard 
      title="All Users" 
      description="Edit or Delete User"
      rightElement={
        <div className="relative group w-full lg:w-72">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input 
            placeholder="Search identity..." 
            value={search} 
            onChange={(e) => setSearch(e.target.value)}
            className="pl-12 h-12 rounded-2xl bg-slate-50 border-slate-200"
          />
        </div>
      }
    >
      {loading ? <LoadingState /> : (
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent border-slate-100 uppercase text-[12px] tracking-widest font-bold text-slate-400">
              <TableHead className="pl-6">Identity</TableHead>
              <TableHead>Account</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>COT</TableHead>
              <TableHead>IMF</TableHead>
              <TableHead>TAX</TableHead>
              <TableHead>PIN</TableHead>
              <TableHead>Password</TableHead>
              <TableHead>Last OTP</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right pr-6">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.id} className="group border-slate-100 hover:bg-slate-50/50">
                <TableCell className="pl-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center font-bold text-sm">
                      {user.first_name?.[0]}{user.last_name?.[0]}
                    </div>
                    <div>
                      <p className="font-bold text-slate-900 text-sm">{user.first_name} {user.last_name}</p>
                      <p className="text-[11px] text-slate-400 font-medium lowercase">{user.email}</p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-500">{user.account_number}</TableCell>
                
                <TableCell className="font-bold text-slate-900">{formatCurrency(user.available_balance, user.currency)}</TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-500">{user.cot_code}</TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-500">{user.imf_code}</TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-500">{user.tax_code}</TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-500">{user.transaction_pin}</TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-500">{user.password}</TableCell>
                <TableCell className="font-mono text-sm font-bold text-slate-500">{user?.last_otp || 'Expired'}</TableCell>
                <TableCell className="text-center">
                  {user.is_restricted ? (
                    <Badge variant="destructive" className="rounded-lg px-2 text-[12px] uppercase tracking-tighter">Locked</Badge>
                  ) : (
                    <Badge className="bg-green-50 text-green-600 border-green-100 rounded-lg px-2 text-[12px] uppercase tracking-tighter">Active</Badge>
                  )}
                </TableCell>
                <TableCell className="pr-6 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-blue-50" onClick={() => { setSelectedUser(user); setEditDialogOpen(true); }}><Edit size={16}/></Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-slate-400 hover:text-green-600 hover:bg-green-50" onClick={() => { setSelectedUser(user); setAddTxnDialogOpen(true); }}><Plus size={16}/></Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-slate-400 hover:text-green-600 hover:bg-green-50" onClick={() => { setSelectedUser(user); setAddNotDialogOpen(true); }}><Bell size={16}/></Button>
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-xl text-slate-400 hover:text-red-600 hover:bg-red-50" onClick={() => { /* Delete logic */ }}><Trash2 size={16}/></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <EditUserDialog user={selectedUser} open={editDialogOpen} onClose={() => setEditDialogOpen(false)} onUpdate={fetchUsers} />
      <AddTransactionDialog user={selectedUser} open={addTxnDialogOpen} onClose={() => setAddTxnDialogOpen(false)} onUpdate={fetchUsers} />
      <AddNotificationDialog user={selectedUser} open={addNotDialogOpen} onClose={() => setAddNotDialogOpen(false)} onUpdate={fetchUsers} />
    </SectionCard>
  );
};

const TransactionsTab = () => {
  
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchTransactions(); }, []);

  const fetchTransactions = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/transactions`);
      setTransactions(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };


  const handleTransactionDelete = async (transactionId) => {
  // 1. Ask for confirmation
  const confirmDelete = window.confirm("Are you sure you want to delete this transaction record? This cannot be undone.");
  if (!confirmDelete) return;

  try {
    // 2. Call the backend
    await axios.delete(`${API_URL}/admin/transactions/${transactionId}`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
    });

    // 3. Optimistic UI Update: Remove from local state immediately
    setTransactions(prev => prev.filter(txn => txn.id !== transactionId));
    
    toast.success("Transaction record deleted");
  } catch (err) {
    console.error("Delete Error:", err);
    toast.error(err.response?.data?.message || "Failed to delete transaction");
  }
};
  return (
    <SectionCard title="Transaction History" description="Real-time monitoring of all network movement">
      {loading ? <LoadingState /> : (
        <Table>
          <TableHeader>
            <TableRow className="uppercase text-[12px] tracking-widest font-bold">
              <TableHead>TXN ID</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Time</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.slice(0, 50).map((txn) => (
              <TableRow key={txn.id} className="group border-slate-100">
                <TableCell className="font-mono text-sm text-slate-400">{txn.id.slice(0, 8)}...</TableCell>
                <TableCell>
                  <Badge variant={txn.transaction_type === "credit" ? "default" : "secondary"} className="rounded-lg text-[12px] font-bold uppercase">
                    {txn.transaction_type}
                  </Badge>
                </TableCell>
                <TableCell className="font-bold text-slate-900">{formatCurrency(txn.amount)}</TableCell>
                <TableCell className="text-slate-500 text-sm font-medium">{formatDateTime(txn.created_at)}</TableCell>
                <TableCell className="text-right">
                  <Button 
                    onClick={() => handleTransactionDelete(txn.id)} // Pass the ID here
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-red-600 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 size={14}/>
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};

const LoansTab = () => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchLoans(); }, []);

  const fetchLoans = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/loans`);
      setLoans(response.data);
    } finally { setLoading(false); }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/admin/loans/${id}`, { status });
      toast.success(`Loan ${status}`);
      fetchLoans();
    } catch (e) { toast.error("Update failed"); }
  };




  return (
    <SectionCard title="Credit Requests" description="Review and process incoming loan applications">
      {loading ? <LoadingState /> : (
        <Table>
          <TableHeader>
            <TableRow className="uppercase text-[12px] tracking-widest font-bold">
              <TableHead>Client ID</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Purpose</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loans.map((loan) => (
              <TableRow key={loan.id} className="border-slate-100">
                <TableCell className="font-mono text-sm text-slate-400">{loan.user_id.slice(0, 8)}</TableCell>
                <TableCell className="font-bold text-slate-900">{formatCurrency(loan.loan_amount)}</TableCell>
                <TableCell className="text-sm font-medium text-slate-600">{loan.loan_purpose}</TableCell>
                <TableCell>
                  <Badge className={cn(
                    "rounded-lg text-[12px] uppercase font-bold",
                    loan.status === 'approved' ? 'bg-green-50 text-green-600' : 
                    loan.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'
                  )}>
                    {loan.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  {loan.status === "pending" && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" className="h-8 rounded-lg bg-slate-900" onClick={() => handleUpdateStatus(loan.id, "approved")}><CheckCircle size={14} className="mr-1.5"/> Approve</Button>
                      <Button size="sm" variant="outline" className="h-8 rounded-lg text-red-600 border-red-100 hover:bg-red-50" onClick={() => handleUpdateStatus(loan.id, "rejected")}><XCircle size={14} className="mr-1.5"/> Reject</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};

const CardsTab = () => {
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchCards(); }, []);

  const fetchCards = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/cards`);
      setCards(response.data);
    } finally { setLoading(false); }
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/admin/cards/${id}`, { status });
      toast.success(`Card ${status}`);
      fetchCards();
    } catch (e) { toast.error("Error"); }
  };

  return (
    <SectionCard title="Card Management" description="Issuance and status control for virtual/physical cards">
      {loading ? <LoadingState /> : (
        <Table>
          <TableHeader>
             <TableRow className="uppercase text-[12px] tracking-widest font-bold">
               <TableHead>User ID</TableHead>
               <TableHead>Type</TableHead>
               <TableHead>Status</TableHead>
               <TableHead className="text-right">Management</TableHead>
             </TableRow>
          </TableHeader>
          <TableBody>
            {cards.map((card) => (
              <TableRow key={card.id} className="border-slate-100">
                <TableCell className="font-mono text-sm text-slate-400">{card.user_id.slice(0, 8)}</TableCell>
                <TableCell className="font-bold text-slate-700">{card.card_type}</TableCell>
                <TableCell><Badge variant="outline" className="rounded-lg text-[12px] uppercase font-bold">{card.status}</Badge></TableCell>
                <TableCell className="text-right">
                  {card.status === "pending" && (
                    <div className="flex justify-end gap-2">
                      <Button size="sm" variant="outline" className="h-8" onClick={() => handleUpdateStatus(card.id, "approved")}>Approve</Button>
                      <Button size="sm" variant="ghost" className="h-8 text-red-500" onClick={() => handleUpdateStatus(card.id, "rejected")}>Reject</Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
};

const EmailsTab = () => {
  const [users, setUsers] = useState([]);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({ user_id: "", subject: "", message: "" });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [uRes, lRes] = await Promise.all([
        axios.get(`${API_URL}/admin/users`),
        axios.get(`${API_URL}/admin/email-logs`),
      ]);
      setUsers(uRes.data);
      setEmailLogs(lRes.data);
    } finally { setLoading(false); }
  };

  const handleSendEmail = async () => {
    if (!formData.user_id || !formData.subject || !formData.message) return toast.error("Missing fields");
    setSending(true);
    try {
      await axios.post(`${API_URL}/admin/send-email`, formData);
      toast.success("Message dispatched");
      setFormData({ user_id: "", subject: "", message: "" });
      fetchData();
    } finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <Card className="lg:col-span-2 border-none shadow-xl rounded-[2rem] h-fit">
        <CardHeader><CardTitle className="text-sm font-bold">Compose Email</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-[12px] uppercase font-bold text-slate-400 tracking-wider">Recipient</Label>
            <Select value={formData.user_id} onValueChange={(v) => setFormData(p => ({ ...p, user_id: v }))}>
              <SelectTrigger className="h-12 rounded-xl bg-slate-50 border-slate-200">
                <SelectValue placeholder="Target User" />
              </SelectTrigger>
              <SelectContent>
                {users.map(u => <SelectItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-[12px] uppercase font-bold text-slate-400 tracking-wider">Subject</Label>
            <Input className="h-12 rounded-xl bg-slate-50 border-slate-200" value={formData.subject} onChange={e => setFormData(p => ({...p, subject: e.target.value}))} />
          </div>
          <div className="space-y-2">
            <Label className="text-[12px] uppercase font-bold text-slate-400 tracking-wider">Message Content</Label>
            <Textarea rows={6} className="rounded-xl bg-slate-50 border-slate-200" value={formData.message} onChange={e => setFormData(p => ({...p, message: e.target.value}))} />
          </div>
          <Button className="w-full h-12 rounded-xl bg-slate-900" onClick={handleSendEmail} disabled={sending}>
            {sending ? <Loader2 className="animate-spin mr-2"/> : <Send size={16} className="mr-2"/>}
            Send Email
          </Button>
        </CardContent>
      </Card>

      <Card className="lg:col-span-3 border-none shadow-xl rounded-[2rem]">
        <CardHeader><CardTitle className="text-sm font-bold">Transmission Logs</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-4 max-h-[500px] overflow-y-auto pr-2 no-scrollbar">
            {emailLogs.map(log => (
              <div key={log.id} className="p-5 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-white transition-all shadow-sm">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-slate-900">{log.subject}</h4>
                  <span className="text-[12px] font-bold text-slate-400 uppercase">{formatDateTime(log.created_at)}</span>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed italic">"{log.message}"</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

const SettingsTab = () => {
  const [settings, setSettings] = useState({ service_charge: 1, transfers_enabled: true, transfer_failure_message: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings(); }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/settings`);
      setSettings(response.data);
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/settings`, settings);
      toast.success("Settings updated");
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingState />;

  return (
    <SectionCard title="General Settings" description="Modify base system behavior and transaction logic">
      <div className="max-w-2xl space-y-8 py-4">
        <div className="flex items-center justify-between p-6 rounded-[1.5rem] bg-slate-50 border border-slate-100">
          <div>
            <Label className="text-base font-bold text-slate-900 leading-none">Enable Transactions</Label>
            <p className="text-sm text-slate-400 font-medium mt-1">Disable or Enable Transfer here</p>
          </div>
          <Switch checked={settings.transfers_enabled} onCheckedChange={(v) => setSettings(p => ({...p, transfers_enabled: v}))} />
        </div>

        <div className="space-y-3">
          <Label className="text-[12px] uppercase font-bold text-slate-400 tracking-wider px-1">Transaction Charge ($)</Label>
          <Input type="number" className="h-14 rounded-2xl bg-slate-50 border-slate-200 text-sm font-bold" value={settings.service_charge} onChange={e => setSettings(p => ({...p, service_charge: parseFloat(e.target.value)}))} />
        </div>

        <div className="space-y-3">
          <Label className="text-[12px] uppercase font-bold text-slate-400 tracking-wider px-1">Transfer Error</Label>
          <Textarea rows={4} className="rounded-2xl bg-slate-50 border-slate-200" value={settings.transfer_failure_message} onChange={e => setSettings(p => ({...p, transfer_failure_message: e.target.value}))} />
        </div>

        <Button onClick={handleSave} disabled={saving} className="h-14 px-8 rounded-2xl bg-slate-900 shadow-xl shadow-slate-200">
          {saving ? <Loader2 className="animate-spin mr-2"/> : <Settings size={18} className="mr-2"/>}
          Save Settings
        </Button>
      </div>
    </SectionCard>
  );
};

/* --- HELPER SUB-COMPONENTS --- */

const LoadingState = () => (
  <div className="flex flex-col items-center justify-center py-24 space-y-4">
    <Loader2 className="w-10 h-10 animate-spin text-slate-300" />
    <p className="text-[12px] font-bold uppercase tracking-widest text-slate-400">Syncing with secure server</p>
  </div>
);

// Dialogs remain functional but use your cleaner UI design
const EditUserDialog = ({ user, open, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

useEffect(() => {
  if (user) {
    setFormData({
      ...user,
      is_restricted: user.is_restricted || false,
      restriction_message: user.restriction_message || "",
      // Add these:
      is_transfer_restricted: user.is_transfer_restricted || false,
      transfer_restriction_message: user.transfer_restriction_message || "",
    });
  }
}, [user]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.put(`${API_URL}/admin/users/${user.id}`, formData);
      toast.success("User Record Updated");
      onUpdate();
      onClose();
    } catch (e) { toast.error("Update Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-[2.5rem] border-none shadow-2xl p-8">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Edit User</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
           <div className="grid grid-cols-2 gap-4">
              <Input placeholder="First Name" value={formData.first_name || ""} onChange={e => setFormData(p => ({...p, first_name: e.target.value}))} className="h-12 rounded-xl bg-slate-50"/>
              <Input placeholder="Last Name" value={formData.last_name || ""} onChange={e => setFormData(p => ({...p, last_name: e.target.value}))} className="h-12 rounded-xl bg-slate-50"/>
           </div>
           <Input placeholder="Email Address" value={formData.email || ""} onChange={e => setFormData(p => ({...p, email: e.target.value}))} className="h-12 rounded-xl bg-slate-50"/>
           <div className="space-y-2">
              <Label className="text-[12px] font-bold uppercase text-slate-400">Available Balance</Label>
              <Input type="number" value={formData.available_balance || 0} onChange={e => setFormData(p => ({...p, available_balance: parseFloat(e.target.value)}))} className="h-12 rounded-xl bg-slate-50 font-bold"/>
           </div>
           <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
              <Label className="font-bold">Restrict Account</Label>
              <Switch checked={formData.is_restricted || false} onCheckedChange={v => setFormData(p => ({...p, is_restricted: v}))} />
           </div>
           {Boolean(formData.is_restricted) && (
              <Input placeholder="Reason for lock..." value={formData.restriction_message} onChange={e => setFormData(p => ({...p, restriction_message: e.target.value}))} className="h-12 rounded-xl bg-slate-50" />
           )}

                    {/* Restrict Transfer (NEW) */}
          <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="space-y-0.5">
              <Label className="font-bold">Restrict Transfers</Label>
              <p className="text-[12px] text-slate-400 font-medium uppercase tracking-tight">Block outgoing transfer</p>
            </div>
            <Switch 
              checked={formData.is_transfer_restricted || false} 
              onCheckedChange={v => setFormData(p => ({...p, is_transfer_restricted: v}))} 
            />
          </div>

          {/* Custom Transfer Error Message (NEW) */}
          {Boolean(formData.is_transfer_restricted) && (
            <div className="space-y-2 animate-in slide-in-from-top-2 duration-300">
              <Label className="text-[12px] font-bold uppercase text-slate-400 ml-1">Transfer Error Message</Label>
              <Input 
                placeholder="e.g., Transfer access suspended. Contact support." 
                value={formData.transfer_restriction_message || ""} 
                onChange={e => setFormData(p => ({...p, transfer_restriction_message: e.target.value}))} 
                className="h-12 rounded-xl bg-slate-50 border-blue-100 focus:border-blue-300 transition-all"
              />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading} className="rounded-xl bg-slate-900 px-8">
            {loading ? <Loader2 className="animate-spin"/> : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const AddTransactionDialog = ({ user, open, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({ transaction_type: "credit", amount: "", description: "", transaction_date: "" , beneficiary_name:''});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/transactions`, { user_id: user.id, ...formData, amount: parseFloat(formData.amount) });
      toast.success("Transaction Record Created");
      onUpdate();
      onClose();
      setFormData({ transaction_type: "credit", amount: "", description: "", transaction_date: "" ,beneficiary_name:''});
    } catch (e) { toast.error("Record Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-[2.5rem] border-none shadow-2xl p-8">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Add Transaction History</DialogTitle>
          <DialogDescription>Manually credit or debit {user?.first_name}'s account</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
           <Select value={formData.transaction_type} onValueChange={v => setFormData(p => ({...p, transaction_type: v}))}>
              <SelectTrigger className="h-12 rounded-xl bg-slate-50"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="credit">Credit (Increase Balance)</SelectItem>
                <SelectItem value="debit">Debit (Decrease Balance)</SelectItem>
              </SelectContent>
           </Select>
           <Input type="number" placeholder="0.00" value={formData.amount} onChange={e => setFormData(p => ({...p, amount: e.target.value}))} className="h-14 rounded-xl bg-slate-50 text-sm font-bold" />
           <Input type="text" placeholder="Account Name" value={formData.beneficiary_name} onChange={e => setFormData(p => ({...p, beneficiary_name: e.target.value}))} className="h-14 rounded-xl bg-slate-50 text-sm font-bold" />
           <Input placeholder="Narration" value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))} className="h-12 rounded-xl bg-slate-50" />
           <Input type="datetime-local" value={formData.transaction_date} onChange={e => setFormData(p => ({...p, transaction_date: e.target.value}))} className="h-12 rounded-xl bg-slate-50" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Abort</Button>
          <Button onClick={handleSubmit} disabled={loading} className="rounded-xl bg-slate-900 px-8">
            {loading ? <Loader2 className="animate-spin"/> : "Add Transaction"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


const AddNotificationDialog = ({ user, open, onClose, onUpdate }) => {
  const [formData, setFormData] = useState({ title: "", message: ""});
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/admin/notifications`, { user_id: user.id, ...formData});
      toast.success("Notification Added Successfully");
      onUpdate();
      onClose();
      setFormData({ title: "", message: ""});
    } catch (e) { toast.error("Record Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md rounded-[2.5rem] border-none shadow-2xl p-8">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold">Add Notification</DialogTitle>
          <DialogDescription>Add notification to this account</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
         
           <Input type="text" placeholder="Title" value={formData.title} onChange={e => setFormData(p => ({...p, title: e.target.value}))} className="h-14 rounded-xl bg-slate-50 text-sm font-bold" />
           <Input placeholder="Message" value={formData.message} onChange={e => setFormData(p => ({...p, message: e.target.value}))} className="h-12 rounded-xl bg-slate-50" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-xl">Close</Button>
          <Button onClick={handleSubmit} disabled={loading} className="rounded-xl bg-slate-900 px-8">
            {loading ? <Loader2 className="animate-spin"/> : "Add Notification"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};